/**
 * Admin User Management — list, search, activate/deactivate users.
 */
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminAPI.getUsers();
        setUsers(Array.isArray(res.data) ? res.data : (res.data?.users || []));
      } catch {
        toast.error('Failed to load users.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleActive = async (userId, currentStatus) => {
    try {
      const targetUser = users.find((u) => (u.uid || u.id) === userId);
      if (!targetUser) {
        toast.error('User not found.');
        return;
      }

      await adminAPI.updateUser({
        uid: userId,
        role: targetUser.role,
        is_active: !currentStatus,
      });

      setUsers(prev => prev.map(u => ((u.uid || u.id) === userId ? { ...u, is_active: !currentStatus } : u)));
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}.`);
    } catch {
      toast.error('Failed to update user status.');
    }
  };

  const filtered = users.filter(u => {
    const matchRole = filter === 'all' || u.role === filter;
    const matchSearch = !search
      || (u.full_name || '').toLowerCase().includes(search.toLowerCase())
      || (u.email || '').toLowerCase().includes(search.toLowerCase());
    return matchRole && matchSearch;
  });

  return (
    <Layout title="User Management" role="admin" showBack>
      <div className="p-4 pb-24 space-y-4">
        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-10"
            placeholder="Search users..."
          />
        </div>

        {/* Filter chips */}
        <div className="flex gap-2">
          {['all', 'patient', 'doctor', 'admin'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1) + 's'}
            </button>
          ))}
        </div>

        <p className="text-sm text-gray-500">{filtered.length} users</p>

        {/* User List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-8 text-gray-400">
            <p className="text-sm">No users found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(user => {
              const userId = user.uid || user.id;

              return (
              <div key={userId} className="card flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  user.role === 'patient' ? 'bg-primary-100' :
                  user.role === 'doctor' ? 'bg-accent-100' : 'bg-gray-200'
                }`}>
                  <span className={`font-bold ${
                    user.role === 'patient' ? 'text-primary-700' :
                    user.role === 'doctor' ? 'text-accent-700' : 'text-gray-700'
                  }`}>
                    {(user.full_name || 'U').charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{user.full_name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'patient' ? 'bg-primary-100 text-primary-700' :
                    user.role === 'doctor' ? 'bg-accent-100 text-accent-700' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {user.role}
                  </span>
                  <button
                    onClick={() => toggleActive(userId, user.is_active !== false)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${
                      user.is_active !== false ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      user.is_active !== false ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserManagement;
